import AddCircleOutlined from "@mui/icons-material/AddCircleOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAllJobs } from "../../../hooks/useJobs";
import { useGetDepartmentNurseUnions } from "../../../pages/Profile/v2/hooks";
import { useAgencies } from "../../../pages/Profile/v2/ProfessionalInfo/useAgencies";
import { type HoursRateConfig, RateHoursBonusType } from "../../../utils/models";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { CONFIG_TABLE_CONTAINER_SX, CONFIG_TABLE_HEADER_CELL_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { HoursRateConfigRow } from "./HoursRateConfigRow";
import type { HoursRateDraftRow } from "./HoursRateTimeCell";
import { actionsColumn, columnWidthSx, HOURS_RATE_COLUMNS, visibleColumns } from "./wageRuleColumns";
import type { WageRulesTableProps } from "./wageRulesTableProps";

const HOURS_RATE_ACTIONS_COLUMN = actionsColumn("department.config.rates.hoursRate.col.actions");

/**
 * The hours-rate (wage differential) table, edited on Team → Wage Settings (DAT-905). Extracted from
 * Settings → Department Configuration's Rates tab, where it lived inline and wrote through the whole
 * department config save.
 *
 * Every edit is a functional update against the latest rows. The inline original built each new list
 * from the render's `formData.hoursRateConfig`, so two updates in one tick (the raw-input marker, then
 * the parsed times) each started from the same stale list.
 */
export const HoursRateConfigTable: FC<WageRulesTableProps<HoursRateConfig>> = ({
	rows,
	setRows,
	roles,
	jobIds,
	departmentId,
	disabled,
	highlightedRowIndices,
}) => {
	const { t } = useTranslation();
	const { agencies } = useAgencies();
	const { data: existingUnions } = useGetDepartmentNurseUnions();
	const { data: allJobs } = useAllJobs();
	const departmentJobs = useMemo(() => {
		const selected = new Set(jobIds ?? []);
		return (allJobs ?? []).filter((j) => selected.has(j.id));
	}, [allJobs, jobIds]);
	const showJobColumn = departmentJobs.length > 0;
	const columns = useMemo(() => [...visibleColumns(HOURS_RATE_COLUMNS, showJobColumn), HOURS_RATE_ACTIONS_COLUMN], [showJobColumn]);

	const bonusTypeOptions = useMemo(
		() => [
			{ value: RateHoursBonusType.FlatRate, label: t("department.config.rates.wageMode.flatRate"), disabled },
			{ value: RateHoursBonusType.Percentage, label: t("department.config.rates.wageMode.percentage"), disabled },
		],
		[t, disabled],
	);

	// Shared by the section-header button and the in-table EmptyState action.
	const addRow = useCallback(() => {
		setRows((prev) => [
			...prev,
			{
				id: undefined,
				name: "",
				weekend: undefined,
				// Not chosen, so the user must pick "All roles" or "Specific roles" — unless the department has no
				// roles (job-managed departments), where there is nothing to pick and the row can only mean all.
				roles: roles.length === 0 ? [] : undefined,
				jobIds: [], // [] = all jobs (default)
				departmentId,
				startTime: null,
				endTime: null,
				bonusVal: 0,
				bonusType: RateHoursBonusType.FlatRate,
				isCharge: undefined,
				trainingStatuses: undefined,
				employmentTypes: undefined,
				agency: undefined,
				// Seeded as "applies to everyone", matching how a pre-union row decodes from the API.
				union: [],
				isUnion: null,
			},
		]);
	}, [setRows, departmentId, roles]);

	const updateRow = useCallback(
		(index: number, update: (row: HoursRateDraftRow) => HoursRateDraftRow) => {
			setRows((prev) => prev.map((row, i) => (i === index ? update(row) : row)));
		},
		[setRows],
	);

	const removeRow = useCallback((index: number) => setRows((prev) => prev.filter((_, i) => i !== index)), [setRows]);

	const addRowButton = (variant: "primary" | "secondary", dataTestId: string) => (
		<SystemButton
			onClick={addRow}
			buttonVariant={variant}
			size="small"
			disabled={disabled}
			leadingIcon={<AddCircleOutlined fontSize={variant === "secondary" ? "small" : undefined} />}
			data-testid={dataTestId}
		>
			{t("department.config.rates.addRow")}
		</SystemButton>
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
			<SettingsSection
				headerOnly
				icon={<ScheduleOutlined />}
				title={t("department.config.rates.section.hoursRate")}
				dataTestId="dept-config-rates-hours-rate-header"
				action={addRowButton("primary", "dept-config-rates-hours-rate-add-row")}
			/>
			<TableContainer component={Paper} sx={CONFIG_TABLE_CONTAINER_SX}>
				<Table sx={{ minWidth: "max-content", width: "100%" }}>
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell key={column.id} align="left" sx={[CONFIG_TABLE_HEADER_CELL_SX, columnWidthSx(column)]}>
									{t(column.headerKey)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row, index) => (
							<HoursRateConfigRow
								key={row.id || index}
								row={row}
								index={index}
								roles={roles}
								departmentJobs={departmentJobs}
								showJobColumn={showJobColumn}
								agencies={agencies}
								existingUnions={existingUnions}
								bonusTypeOptions={bonusTypeOptions}
								updateRow={updateRow}
								removeRow={removeRow}
								disabled={disabled}
								highlighted={highlightedRowIndices.has(index)}
							/>
						))}
						{rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={columns.length} sx={{ borderBottom: "none", p: 0 }}>
									<EmptyState
										icon={<ScheduleOutlined />}
										title={t("department.config.rates.hoursRate.empty.title")}
										hint={t("department.config.rates.hoursRate.empty.hint")}
										action={addRowButton("secondary", "dept-config-rates-hours-rate-empty-add")}
										dataTestId="dept-config-rates-hours-rate-empty"
									/>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
